import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { EuroGameRow } from "@/components/euro/EuroGames";
import { useEuroLive } from "@/hooks/useEuroLive";
import "@/components/standings/standings.css";
import "@/components/euro/euro.css";

const age = (born) => born && Math.floor((Date.now() - new Date(born)) / 31557600000);

function Roster({ players }) {
  return (
    <section className="ncs-panel" data-testid="europe-roster">
      <h2><span className="ncs-east" />ROSTER</h2>
      <div className="ncs-scroll">
        <table>
          <thead><tr>{["#", "PLAYER", "POS", "GP", "MIN", "PTS", "REB", "AST", "3PM", "PIR", "HEIGHT", "AGE", "NATIONALITY"].map((h) => <th key={h} className={h === "PLAYER" ? "ncs-team-col" : ""}>{h}</th>)}</tr></thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.code} data-testid={`europe-player-${p.code}`}>
                <td className="ncs-seed">{p.jersey || "—"}</td>
                <td className="ncs-team-col"><span className="eu-player">{p.photo ? <img src={p.photo} alt="" loading="lazy" /> : <i />}{p.name}</span></td>
                <td>{p.position || "—"}</td>
                {p.stats ? ["gp", "min", "pts", "reb", "ast", "fg3m", "pir"].map((k) => <td key={k} className={k === "pir" ? "eu-pir" : ""} data-testid={`europe-player-${k}-${p.code}`}>{k === "gp" ? p.stats.gp : p.stats[k].toFixed(1)}</td>)
                  : <td colSpan={7} className="ncs-muted" data-testid={`europe-player-nostats-${p.code}`}>No games played</td>}
                <td>{p.height ? `${p.height} cm` : "—"}</td><td>{age(p.born) || "—"}</td><td>{p.country || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!players.length && <p className="ncs-state">Roster not available yet.</p>}
    </section>
  );
}

export default function EuroTeam() {
  const { comp, code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const live = useEuroLive();
  useEffect(() => {
    setData(null); setError("");
    api.get(`/euro/${comp}/teams/${code}`).then((r) => setData(r.data)).catch((e) => setError(errorText(e)));
  }, [comp, code]);

  const played = data ? data.schedule.filter((g) => g.played).reverse() : [];
  const upcoming = data ? data.schedule.filter((g) => !g.played) : [];
  const s = data?.standing;
  return (
    <div className="ncs-root eu-root" data-testid="europe-team-page">
      <Link to={`/europe/${comp}`} className="eu-back" data-testid="europe-team-back"><ArrowLeft size={15} />{comp === "E" ? "EuroLeague" : "EuroCup"} standings</Link>
      {error && <p className="ncs-state" role="alert" data-testid="europe-team-error">{error}</p>}
      {!data && !error && <p className="ncs-state" data-testid="europe-team-loading">Loading team…</p>}
      {data && (
        <>
          <header className="eu-team-head">
            {data.club.crest && <img src={data.club.crest} alt="" />}
            <div>
              <div className="ncs-eyebrow">{data.name.toUpperCase()} · {[data.club.city, data.club.country].filter(Boolean).join(", ").toUpperCase()}</div>
              <h1 data-testid="europe-team-name">{data.club.name}</h1>
              <p>{data.coach && <>Head coach <b>{data.coach}</b></>}{data.club.website && <> · <a href={data.club.website} target="_blank" rel="noreferrer">Official site</a></>}</p>
            </div>
            {s && (
              <div className="eu-team-stats" data-testid="europe-team-record">
                <span><b>{`${s.w}-${s.l}`}</b>Record</span><span><b>{`#${s.position}`}</b>{s.group === "Regular Season" ? "Standing" : `Group ${s.group}`}</span>
                <span><b className={String(s.diff).startsWith("-") ? "ncs-negative" : "ncs-positive"}>{s.diff}</b>Point diff</span><span><b>{s.home}</b>Home</span><span><b>{s.away}</b>Away</span>
              </div>
            )}
          </header>
          <div className="ncs-layout">
            <section className="ncs-main"><Roster players={data.players} /></section>
            <aside className="ncs-sidebar">
              <section className="ncs-card" data-testid="europe-team-results">
                <h3>RESULTS</h3>
                {played.map((g) => <EuroGameRow key={g.id} comp={comp} g={g} focus={code} />)}
                {!played.length && <p className="ncs-muted">No games played yet.</p>}
              </section>
              <section className="ncs-card" data-testid="europe-team-schedule">
                <h3>SCHEDULE</h3>
                {upcoming.slice(0, 12).map((g) => <EuroGameRow key={g.id} comp={comp} g={g} live={live} />)}
                {!upcoming.length && <p className="ncs-muted">No upcoming games.</p>}
              </section>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
